// © 2025 NM Software Development UG (haftungsbeschränkt)
// All rights reserved.

import {ChangeDetectionStrategy, Component, computed, input} from '@angular/core';

/**
 * How loud an {@link InlineNoticeComponent} is.
 *
 * Three values, not a free-form colour: the tone decides the palette, the glyph **and** the ARIA role
 * together, and those three have to move as one. A caller that could pass `'red'` would sooner or later paint
 * a blocking refusal in a colour a screen reader announces politely — or never announces at all.
 *
 * * `error` — the action is refused right now. The user cannot proceed until something changes.
 * * `warning` — the action will happen, but not the way the form currently reads (a value was coerced, a
 *   choice was taken away).
 * * `info` — a plain fact about the form's state. Nothing is wrong and nothing was changed.
 */
export type InlineNoticeTone = 'error' | 'warning' | 'info';

/**
 * A short, in-form message that explains the state of the fields it sits under (#664).
 *
 * **Why a component and not four hand-written boxes.** The events creation form grew four mutually exclusive
 * hints — one refusal, two "your choice was taken away" sentences and one allowance counter — and each was
 * written where it was needed: a rounded red callout with an icon for the refusal, bare grey `<p>`s for the
 * other three. Side by side they read as four unrelated things rather than one slot that changes its message,
 * and the two that mattered least were the two that looked like body copy. One control fixes both halves at
 * once: identical geometry for every state, and the only thing a caller chooses is how loud it is.
 *
 * **Geometry is the same for all three tones — deliberately.** `flex items-center gap-x-2 rounded-md
 * border-[1px] border-solid px-3 py-2 text-sm font-medium`, with an `h-5 w-5 flex-none` glyph. Only the three
 * colours and the glyph change. A warning that is also *smaller* than an error says the same thing twice and
 * makes a row of notices ragged when two states alternate in one slot.
 *
 * **`border-[1px]`, never `border`.** Bootstrap is loaded globally and its `.border` is a **shorthand** with
 * `!important`, so it would nail this edge to Bootstrap grey `#dee2e6` and defeat the Tailwind border colour
 * standing right next to it. Same story for `.rounded` (Bootstrap's is .375 rem, Tailwind's .25 rem) — hence
 * `rounded-md`. See the style guide, "Bootstrap is loaded globally — never write its utility names".
 *
 * **Contrast, measured** (text on surface, sRGB, WCAG 2.1 — all ≥ 4.5:1, so AA holds at `text-sm`):
 *
 * | Tone | Text | Surface | Ratio |
 * | --- | --- | --- | --- |
 * | `error` | `text-red-700` `#b91c1c` | `bg-red-50` `#fef2f2` | **5.91:1** |
 * | `warning` | `text-amber-800` `#92400e` | `bg-amber-50` `#fffbeb` | **6.84:1** |
 * | `info` | `text-gray-700` `#374151` | `bg-gray-50` `#f9fafb` | **9.86:1** |
 *
 * The borders are framing, not meaning — the text carries the message and the surface carries the tone — so
 * they are not held to the 3:1 non-text threshold (`border-red-600` 4.41:1, `border-amber-500` 2.07:1 and
 * `border-gray-300` 1.41:1 against their own surfaces). They exist so the box has an edge against the form's
 * grey panel, which is the job an outline actually does here.
 *
 * **The role follows the tone, and that is the whole reason the tone is not just a colour.** `error` is
 * `role="alert"` (an assertive live region: the submit button just became unreachable, and a user who is not
 * looking at this corner of the form needs to be told now); everything else is `role="status"` (polite — it
 * waits for a pause). Both are announced when the notice appears in an `@if` chain, which is exactly how this
 * slot is used.
 *
 * **When NOT to use it.** This is for a message that belongs *to the fields around it* and stays on screen as
 * long as that state does. A transient refusal from the server — a failed save, a 403 — goes to
 * `GlobalAlertService` / `app-alert-banner` instead; see the style guide's "There is no per-screen error
 * banner".
 *
 * ```html
 * <cqme-inline-notice tone="error" id="event-kind-refusal">{{ refusalReason }}</cqme-inline-notice>
 * ```
 */
@Component({
  selector: 'cqme-inline-notice',
  standalone: true,
  templateUrl: './inline-notice.component.html',
  // The geometry is static and the palette is three class toggles. Written as individual `[class.x]` host
  // bindings rather than one `[class]="…"` string on purpose: a whole-map class binding on a host that also
  // carries static classes composes in a way nobody can read off the source, and a caller is free to put its
  // own `class="…"` on the element (the events form does not, but the next placement will).
  host: {
    'class': 'flex items-center gap-x-2 rounded-md border-[1px] border-solid px-3 py-2 text-sm font-medium',
    '[class.border-red-600]': "tone() === 'error'",
    '[class.bg-red-50]': "tone() === 'error'",
    '[class.text-red-700]': "tone() === 'error'",
    '[class.border-amber-500]': "tone() === 'warning'",
    '[class.bg-amber-50]': "tone() === 'warning'",
    '[class.text-amber-800]': "tone() === 'warning'",
    '[class.border-gray-300]': "tone() === 'info'",
    '[class.bg-gray-50]': "tone() === 'info'",
    '[class.text-gray-700]': "tone() === 'info'",
    '[attr.role]': 'role()',
    '[attr.data-tone]': 'tone()'
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InlineNoticeComponent {
  /**
   * How loud the notice is. Defaults to `info` — the tone that claims the least, so an un-set input
   * under-states rather than crying wolf in red.
   */
  readonly tone = input<InlineNoticeTone>('info');

  /**
   * `alert` for a refusal, `status` for everything else.
   *
   * A getter rather than a literal in the template because the two roles are not interchangeable and the
   * choice belongs beside the tone it follows: `alert` interrupts, `status` waits for a pause. A warning that
   * interrupted every time a contract coerced the event kind would train people to ignore the one that does
   * not.
   */
  protected readonly role = computed<'alert' | 'status'>(() => this.tone() === 'error' ? 'alert' : 'status');

  /**
   * The glyph's `d`, chosen by tone.
   *
   * All three are 16×16 **fill**-style paths whose inner shapes are wound the opposite way round from the
   * outer silhouette, so the bang or the "i" is punched out by the non-zero fill rule instead of being a
   * second coloured path. That is what lets one `fill="currentColor"` carry the whole icon and follow the
   * tone's text colour for free.
   */
  protected readonly iconPath = computed(() => ICON_PATHS[this.tone()]);
}

/**
 * The three glyphs. Outside the class because they are constants, not state, and a 300-character `d` inside a
 * `computed` is unreadable in a diff.
 *
 * `warning` is Bootstrap Icons' `exclamation-triangle-fill`; `error` is the exclamation-circle the events
 * form's refusal callout already used (kept byte-identical so the state whose look was signed off does not
 * change); `info` is the same circle with the bang inverted into a dot-over-bar.
 */
const ICON_PATHS: Readonly<Record<InlineNoticeTone, string>> = {
  error: 'M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1m0 3a.75.75 0 0 1 .75.75v4a.75.75 0 0 1-1.5 0v-4A.75.75 0 0 1 8 4'
    + 'm0 7.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2',
  warning: 'M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 '
    + '1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 '
    + '1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2',
  info: 'M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1M8 3.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2M8 7a.75.75 0 0 1 .75.75v3.5a.75'
    + '.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 8 7'
};
